import {
  amyQuote,
  closingSection,
  fairMarkingSection,
  footer,
  liveIndexable,
  navigation,
  parentFaqs,
  practiceJourney,
  pricingSection,
  SEAT_PRICE,
} from "./shared";
import type { LandingSeo, LandingVariant } from "./types";

/** One page per nav item (GitHub #325). Copy is lindsaymayo's from #322–#343. */

function seo(path: string, title: string, description: string): LandingSeo {
  return {
    title: `${title} — RevisionLab`,
    metaDescription: description,
    canonical: `https://revisionlab.ai${path}`,
    ogTitle: title,
    ogDescription: description,
    ogImage: "/og/default.png",
  };
}

/** GitHub #322 (How it works). Focus sessions (#323) are on the home page. */
export const howItWorksVariant = {
  slug: "how-it-works",
  path: "/how-it-works",
  navigation,
  sections: [
    {
      kind: "hero",
      copy: {
        pill: "How it works",
        title: "Real exam-style practice, one question at a time.",
        subtitle:
          "They pick a subject and a topic, answer in their own words, then see exactly where the marks went.",
      },
    },
    {
      ...practiceJourney,
      copy: { ...practiceJourney.copy, quote: { ...amyQuote, afterStep: 2 } },
    },
    fairMarkingSection,
    closingSection,
  ],
  footer,
  seo: seo(
    "/how-it-works",
    "How it works",
    "Exam-style questions answered in their own words, marked against the official mark scheme, and built to be fair.",
  ),
  publication: liveIndexable,
} as const satisfies LandingVariant;

export const forParentsVariant = {
  slug: "for-parents",
  path: "/for-parents",
  navigation,
  sections: [
    {
      kind: "hero",
      copy: {
        pill: "Support without taking over",
        title: "Less chasing. More reassurance.",
        subtitle:
          "RevisionLab gives your child a clear place to start, with engaging questions and focused sessions that help them build a more independent revision routine.",
      },
    },
    {
      kind: "story",
      copy: {
        paragraphs: [
          "You do not need to be a subject expert or mark their answers. Feedback shows where marks were earned and what to improve next.",
          "With supported subjects and exam boards together in one place, revision is simpler to organise — leaving you more room to encourage, rather than direct every step.",
        ],
      },
    },
    closingSection,
  ],
  footer,
  seo: seo(
    "/for-parents",
    "For parents",
    "Help your child revise more independently with focused practice and useful feedback, without needing to be a subject expert or mark their answers.",
  ),
  publication: liveIndexable,
} as const satisfies LandingVariant;

/** GitHub #326. */
export const aiFaqsVariant = {
  slug: "ai-faqs",
  path: "/ai-faqs",
  navigation,
  sections: [
    {
      kind: "hero",
      copy: {
        pill: "AI",
        title: "AI feedback. A real marking standard.",
        subtitle:
          "At RevisionLab we use AI supplied with course specifications and official mark schemes. The learner answers questions first, then uses the feedback to understand missing marks, practise again and improve their work. RevisionLab recommends the topics where they need the most practice, so their practice follows their strengths and weaknesses.",
      },
    },
    {
      kind: "faq",
      copy: {
        research: {
          title: "Research behind the approach",
          body: "Retrieval practice and implementation intentions inform our answer-first practice and clear session goals.",
          sources: [
            {
              label: "Roediger & Karpicke (2006) — retrieval practice and retention",
              href: "https://doi.org/10.1111/j.1467-9280.2006.01693.x",
            },
            {
              label: "Gollwitzer & Sheeran (2006) — plans and goal achievement",
              href: "https://doi.org/10.1016/S0065-2601(06)38002-1",
            },
          ],
        },
        faqTitle: "Questions parents ask",
        items: [
          ...parentFaqs,
          {
            question: "Is this just a shortcut?",
            answer: "No. Your child answers first, in their own words, then uses the feedback to see where marks were lost and practise again. It is built for understanding, not for copying answers.",
          },
          {
            question: "What can I see as a parent?",
            answer: "Today you can fund a seat and manage billing. A guardian overview of time spent, days studied and topic progress is being built and coming soon.",
          },
          {
            question: "Which subjects and exam boards?",
            answer: "AQA, Edexcel, OCR and WJEC, at GCSE and A-level, across Biology, Business, Chemistry, Economics, English Language, English Literature, French, Geography, German, History, Italian, Mathematics, Physics, Psychology, Sociology and Spanish. Not every subject is offered by every board at every level. One seat covers every supported subject.",
          },
          {
            question: "What data do you collect?",
            answer: "First name, email, and birth month and year, so we can apply the right protections for their age. We do not ask for a surname or a full date of birth. Analytics and reminder emails are off until you switch them on.",
          },
          {
            question: "How do I cancel?",
            answer: "You can cancel any time, from the billing portal.",
          },
        ],
      },
    },
    closingSection,
  ],
  footer,
  seo: seo(
    "/ai-faqs",
    "AI FAQs",
    "How RevisionLab's AI marks against official mark schemes, and the questions parents ask about accuracy, cheating, data and cancelling.",
  ),
  publication: liveIndexable,
} as const satisfies LandingVariant;

/** GitHub #343 (pricing, treated as section 7). The tutor comparison (#324) is on the home page. */
export const pricingVariant = {
  slug: "pricing",
  path: "/pricing",
  navigation,
  sections: [
    {
      kind: "hero",
      copy: {
        pill: "Simple launch pricing",
        title: "One seat. Every supported subject and board.",
      },
    },
    pricingSection,
    closingSection,
  ],
  footer,
  seo: seo(
    "/pricing",
    "Pricing",
    `${SEAT_PRICE} a month per seat for every supported subject and exam board. Cancel anytime.`,
  ),
  publication: liveIndexable,
} as const satisfies LandingVariant;

export const ourStoryVariant = {
  slug: "our-story",
  path: "/our-story",
  navigation,
  sections: [
    {
      kind: "hero",
      copy: {
        pill: "Built from a practical need",
        title: "The story behind RevisionLab",
        subtitle:
          "While tutoring his nephew through A-levels, Isambard saw the gap between knowing a subject and showing it in an exam answer. RevisionLab grew from that practical need: focused practice with feedback grounded in the marking standard.",
      },
    },
    closingSection,
  ],
  footer,
  seo: seo(
    "/our-story",
    "Our story",
    "RevisionLab grew from tutoring through A-levels: focused practice with feedback grounded in the marking standard.",
  ),
  publication: liveIndexable,
} as const satisfies LandingVariant;
